// Where to send somebody back to when they leave our pages mid-sign-in and
// return through a link in an email (#1831).
//
// A relying party's authorization request lives in React Router history state
// while the user is on our domain. History state cannot cross a tab boundary,
// and a password-reset link always opens a new one — so without something in
// the URL, setting a new password strands the user on our site with the
// partner's request still waiting in a tab they have to think of going back to.

import { appConfig } from '@/lib/env';

/** The one path a pending sign-in can legitimately resume at. Strict equality,
 *  matching `PublicRoute`'s exemption test — a prefix match here would be an
 *  open-redirect lever on a link we put in an email. */
const OIDC_CONTINUE_PATH = '/oidc/continue';

/**
 * `request_id` as `/authorize` mints it: 32 random bytes, base64url, 43 chars
 * (`OPAQUE_TOKEN_PATTERN` in `functions/src/oidc/store.ts`).
 */
const REQUEST_ID = /^[A-Za-z0-9_-]{43}$/;

/**
 * The absolute URL to hand Firebase as a password-reset continue destination,
 * or `undefined` when there is no pending sign-in to resume.
 *
 * **Rebuilt from the parsed `request_id`, never passed through.** That is the
 * property doing the work: this string goes into an email and is followed later
 * by a browser in another tab, and a URL assembled from one validated
 * 43-character token plus constants cannot carry anything we did not put there
 * — no extra parameter, no fragment, no second path segment.
 *
 * Because it is rebuilt, **extra parameters arriving are harmless, and are
 * simply dropped.** An earlier version refused them instead, which sounded
 * stricter and was a bug: `/authorize` puts `prompt` and `login_hint` on this
 * URL beside `request_id` (`functions/src/api/routes/oidc.ts`), and the partner
 * pack tells relying parties to send `login_hint` on every request where they
 * know the address. Refusing meant this did nothing at all for exactly the
 * migration path it exists to serve — with no error and no log.
 *
 * `login_hint` is dropped rather than carried, deliberately. It is a prefill
 * convenience, and the reset address is typed by whoever is at the keyboard and
 * need not be the account holder's — carrying it would put one person's email
 * address into a message sent to another's.
 *
 * `appConfig.app.url` rather than `import.meta.env.VITE_APP_URL`, because
 * `appConfig` already fails at module load when the variable is missing — so
 * this cannot quietly produce a relative URL.
 */
export function pendingSignInContinueUrl(state: unknown): string | undefined {
  const returnTo = (
    state as { returnTo?: { pathname?: unknown; search?: unknown } } | null
  )?.returnTo;
  if (!returnTo || typeof returnTo.pathname !== 'string') {
    return undefined;
  }
  if (returnTo.pathname !== OIDC_CONTINUE_PATH) {
    return undefined;
  }
  if (typeof returnTo.search !== 'string') {
    return undefined;
  }
  const requestId = new URLSearchParams(returnTo.search).get('request_id');
  if (requestId === null || !REQUEST_ID.test(requestId)) {
    return undefined;
  }

  return `${appConfig.app.url}${OIDC_CONTINUE_PATH}?request_id=${requestId}`;
}
